/* sticky-atc-bar.js - the PDP's bottom bar. One rule, no state of its own.

   THE RULE, AND THERE IS EXACTLY ONE, FOR BOTH FORMATS: the bar is shown once
   the shopper has SCROLLED PAST the real add to cart button, and hidden while
   that button is still ahead of them or back within reach. Nothing here asks
   how wide the screen is and nothing here has a breakpoint. A `@media` rule
   would have been a second answer to a question the intersection already
   answers, and the two would disagree on a 900px tablet.

   "PASSED" AND "NOT IN THE VIEWPORT" ARE NOT THE SAME PREDICATE, and the whole
   of this file's history is in the difference. A button is outside the viewport
   in TWO OPPOSITE situations: below it, not reached yet, and above it, already
   left behind. The first version of this rule showed the bar on
   `!isIntersecting`, which is true in both, and that one conflation produced
   BOTH defects reported on 2026-09-19. MEASURED with
   state/tools/measure-pdp-fold.mjs on the served dev theme, on
   la-roche-posay-cicaplast-baume-b5: at 390x844 that button opens at top 1025,
   229px BELOW the fold. So at load it was not intersecting and the bar stood
   over a button the shopper had not reached; scrolling down brought that button
   into view and the bar left; the button then went off the top and the bar came
   back. Three states for one downward gesture, which is the "il se retire et
   puis il se remet" of the report. At 1440x900 the same button opens at top 558,
   INSIDE the fold, so that format never met the first two of the three and the
   defect read as mobile-only. Reading the DIRECTION instead makes the state
   monotonic in scroll position: hidden above the button, shown below it, one
   transition each way. docs/PLAN.md P6b accepted the old show-at-load; this
   supersedes it, and state/tools/verify-sticky-atc.mjs carries the new rule.

   THE TWO LINES, SO THE BOUNDARY CANNOT CHATTER. Shown when the button's bottom
   passes the viewport's top edge; hidden only once 44px of it is back inside,
   which is --pp-tap-target from snippets/design-tokens.liquid and not a number
   invented here: this bar stands in for a button the thumb cannot reach, so it
   has no reason to leave before that button offers a full tap target again. A
   shopper resting exactly on the edge therefore keeps whichever state they
   arrived in. A CSS transition would have smoothed the LOOK of the flicker and
   left the state oscillating underneath, so the remedy is here and not there.

   The instrument is an IntersectionObserver on the button itself rather than a
   scroll listener comparing offsets, for the ordinary reason and for one that
   is specific to this page: the info column is `position: sticky` from 1024px,
   so the button's document offset and its viewport position stop agreeing as
   soon as the column pins. The observer measures the thing the rule is actually
   about.

   COUPLING 1, THE VARIANT REPAINT, AND IT IS THE REASON THIS FILE IS NOT TEN
   LINES. assets/variant-picker.js re-renders the section server side on every
   option change and swaps the innerHTML of every `[data-variant-region]`. The
   real add button lives inside the `buy` region, so THE NODE THIS OBSERVER IS
   POINTED AT IS DESTROYED ON EVERY VARIANT CHANGE. An observer left pointing at
   a detached node reports nothing at all, so the bar would freeze in whatever
   state it happened to hold and then lie about it for the rest of the page
   view: shown over a button the shopper has scrolled back to, or missing under
   one they cannot reach. So aim() re-queries the button after every repaint,
   unobserves the old node and observes the new one, and it is armed from
   PP.EVENTS.variantChange, which variant-picker publishes AFTER patch() has
   run. The bar's own root is deliberately the region element rather than
   something inside it, because patch() replaces a region's children and keeps
   the region: the `is-shown` class written below therefore survives the switch
   and the bar does not blink out and back in under the shopper's thumb.

   COUPLING 2, THE ASSOCIATION. The submit button is not inside the form it
   posts; it is bound to it by the HTML `form` attribute, exactly as the live
   theme binds its own. The form id is built in snippets/buy-buttons.liquid from
   the id Shopify generates for the buy-buttons THEME BLOCK, and a snippet
   rendered by the SECTION cannot read a nested theme block's data back without
   leaving documented ground (sections/collapsible-tabs.liquid refuses the same
   access in the same words). So this file writes the attribute, from the form it
   already had to find, and re-writes it after every repaint because the button
   inside the bar is a new node too. couple() ends by asking the BROWSER whether
   the association resolved, `submit.form === form`, rather than trusting the
   string it just wrote, and a bar that cannot answer yes is never shown: a
   button that posts nothing is worse than no button.

   COUPLING 3, THE EDITOR. shopify:section:load fires on every merchant edit and
   fires NO matching unload, so a teardown reachable only through the old root
   is a teardown that never runs. The registry is therefore keyed by SECTION ID,
   the one handle that survives the node being replaced, and it is swept for
   entries whose node has left the document.

   THE SPACE IT TAKES IS GIVEN BACK. Being fixed, the bar is invisible to layout
   and would otherwise sit on top of the end of the document forever. Its
   measured height is published as --pp-sticky-atc-height on the root and
   assets/base.css spends it on the body's padding-block-end and the root's
   scroll-padding-bottom, added to --pp-compare-bar-height rather than sharing
   it: the two bars are published by two different modules and a shared property
   would mean whichever wrote last erased the other. The sum is honest because
   the bar sits ON TOP of the compare bar rather than over it, see the
   `inset-block-end` in sections/main-product.liquid.

   MOTION IS CSS ONLY, one transition on transform and visibility, declared in
   that same stylesheet. No Element.animate and no scroll carrying a behavior,
   which are the two families the reduced motion cap in base.css cannot reach
   (state/tools/motionscope.mjs is the guard, state/lessons.md the scar). */
(function () {
  'use strict';

  var PP = window.PP;
  if (window.ppStickyAtcLoaded) return;
  window.ppStickyAtcLoaded = true;

  var ROOT = '[data-sticky-atc]';
  var HOST = '[data-product-section]';
  var HEIGHT = '--pp-sticky-atc-height';
  /* The hide line, in CSS pixels below the viewport's top edge. It is
     --pp-tap-target from snippets/design-tokens.liquid, read here as a number
     because an IntersectionObserver rootMargin takes no custom property. See
     THE TWO LINES above for why the hide line is not the show line. */
  var REACHABLE = 44;

  /* Keyed by section id, and iterable so it can be swept. See COUPLING 3. */
  var live = Object.create(null);

  function release(id) {
    var entry = live[id];
    if (!entry) return;
    delete live[id];
    entry.stop();
  }

  function sweep() {
    Object.keys(live).forEach(function (id) {
      if (!live[id].node.isConnected) release(id);
    });
  }

  function setup(bar) {
    var host = bar.closest ? bar.closest(HOST) : null;
    if (!host) return;
    var sectionId = host.getAttribute('data-section-id') || '';
    if (!sectionId) return;

    release(sectionId);

    var off = [];
    var watched = null;
    var shown = false;
    var published = -1;

    function on(target, type, handler) {
      target.addEventListener(type, handler);
      off.push(function () {
        target.removeEventListener(type, handler);
      });
    }

    /* Only a SHOWN bar occupies anything, and the class is read rather than the
       flag recomputed, so this can never drift away from render(). The rect is
       honest mid transition: translateY does not scale a box, so the height is
       the same at rest, at 100% and anywhere between. Written only on a changed
       rounded value, because the padding this publishes can itself summon a
       scrollbar and narrow the viewport by a pixel or two. */
    function publishHeight() {
      var next = 0;
      if (bar.classList.contains('is-shown')) next = Math.round(bar.getBoundingClientRect().height);
      if (next === published) return;
      published = next;
      document.documentElement.style.setProperty(HEIGHT, next + 'px');
    }

    function render(next) {
      if (next === shown) return;
      shown = next;
      bar.classList.toggle('is-shown', next);
      publishHeight();
    }

    /* The only place the state can change, and it reads GEOMETRY rather than
       `isIntersecting`, because that flag is the predicate that conflated the
       two sides of the viewport. `boundingClientRect` is in viewport
       coordinates whatever the observer's rootMargin, so both callers compare
       against an absolute line and neither needs `rootBounds`, which a browser
       is allowed to hand back as null.

       Each caller answers for ONE direction and can only push the state its own
       way: the gate can show and never hide, the latch can hide and never show.
       That is what keeps the band between the lines a place where nothing
       happens, instead of a place where the last observer to speak wins.

       Stale entries are dropped rather than acted on. The observer delivers at
       the end of a frame, so a callback can still be carrying the button that
       aim() has already replaced, and acting on it would set the bar from a node
       that has left the page. */
    function decide(entries, line, wanted) {
      for (var i = 0; i < entries.length; i += 1) {
        if (entries[i].target !== watched) continue;
        var bottom = entries[i].boundingClientRect.bottom;
        if (wanted ? bottom <= line : bottom > line) render(wanted);
      }
    }

    /* THE ROOTS ARE HALF PLANES OPEN DOWNWARD, AND THAT IS THE LOAD BEARING
       DETAIL OF THIS FILE. An IntersectionObserver delivers a callback when its
       target's intersection with ITS root CHANGES, so the two situations the rule
       has to tell apart must be two DIFFERENT intersection states. Against the
       plain viewport they are not: a button BELOW the fold and a button ABOVE the
       top are both simply "not intersecting", so a shopper who goes from one to
       the other without the button ever crossing the screen gets NO CALLBACK and
       the bar keeps a state that is now wrong.

       MEASURED, and by the bench rather than by reading: with the viewport as
       root, state/tools/verify-sticky-atc.mjs jumped 390x844 from scrollY 0
       (button bottom 1191, below the fold) straight to the end of the document
       (bottom -1192) and the bar stayed away -- shown-rule, shown-visibility,
       shown-focusable, shown-in-a11y-tree and space-reserved-scroll all red on
       mobile and green on desktop, because at 1440x900 the button STARTS on
       screen and the jump therefore does change the flag. That asymmetry is the
       signature. It is not a fixture artefact: a reload restoring its scroll
       position, a `#fragment` landing, and a back navigation all arrive the same
       way, past a button that was never on screen.

       So each root is extended DOWNWARD past the end of any document, which makes
       `isIntersecting` mean exactly "the button's bottom is still below my top
       edge" -- true when the button is ahead of the shopper, false once it has
       been left behind. Two states, never the same one twice, so every passage
       between them is delivered however abruptly it happens.

       `gate` keeps the viewport's own top edge, so it turns false when the button
       goes fully past: that is the show line at 0. `latch` has that edge pulled
       REACHABLE px down, so it turns true again once 44px of the button is back:
       that is the hide line. Between them neither speaks, which is the band. */
    /* 100000px is "past the end of any document this theme will render", written
       as a number because rootMargin takes no keyword for it. Only the TOP value
       carries meaning; the bottom one exists to make the root a half plane. */
    var gate = new IntersectionObserver(
      function (entries) {
        decide(entries, 0, true);
      },
      { rootMargin: '0px 0px 100000px 0px' }
    );

    var latch = new IntersectionObserver(
      function (entries) {
        decide(entries, REACHABLE, false);
      },
      { rootMargin: '-' + REACHABLE + 'px 0px 100000px 0px' }
    );

    var sizer = null;
    if (typeof ResizeObserver === 'function') {
      sizer = new ResizeObserver(publishHeight);
      sizer.observe(bar);
    } else {
      on(window, 'resize', publishHeight);
    }

    /* The association, re read from the live DOM on every call. The last line is
       the whole point: it asks the browser whether the id it just wrote really
       resolves, instead of believing the string. A merchant who removed the buy
       buttons block, or a form the platform renamed, both answer false here and
       the bar stays away.

       `form.getAttribute('id')` AND NEVER `form.id`, and this is not style. A
       form element's named property getter is [LegacyOverrideBuiltIns] in the
       HTML spec, so a control named `id` SHADOWS the element's own id property,
       and this form always has one: snippets/buy-buttons.liquid posts the
       variant through `input[name="id"]`. MEASURED in Chromium by
       state/tools/verify-sticky-atc.mjs, which is how this line was found:
       `form.id` returned the input element and the attribute went out as
       "[object HTMLInputElement]", so the button resolved to no form at all and
       the bar was never shown on either format. */
    function couple() {
      var submit = bar.querySelector('[data-sticky-atc-submit]');
      var form = host.querySelector('[data-buy-form]');
      var formId = form ? form.getAttribute('id') : '';
      if (!submit || !formId) return false;
      if (submit.getAttribute('form') !== formId) submit.setAttribute('form', formId);
      return submit.form === form;
    }

    /* Idempotent, and it is called on the first paint and after every repaint.
       Nothing below compares node identity to decide whether work is needed
       except the observer swap itself, which is the one place identity is the
       actual question. */
    function aim() {
      var wanted = couple() ? host.querySelector('[data-buy-submit]') : null;
      if (wanted !== watched) {
        if (watched) {
          gate.unobserve(watched);
          latch.unobserve(watched);
        }
        watched = wanted;
        if (watched) {
          /* Both observers deliver one callback for the node they are handed, so
             a fresh button after a repaint re-derives the state from where the
             page actually stands. If it lands inside the band neither speaks and
             the class already on the region root is what survives, which is the
             no-blink property snippets/sticky-atc-bar.liquid depends on. */
          gate.observe(watched);
          latch.observe(watched);
        }
      }
      if (!watched) render(false);
    }

    if (PP && PP.subscribe) {
      /* PP.subscribe rather than a document listener because it hands back an
         unsubscribe, which is what makes the teardown above reachable. The
         section filter is the same one assets/product-gallery.js applies: a page
         can hold a second product section, the quick add panel being the one
         that really happens, and its switches are not this bar's business. */
      off.push(
        PP.subscribe(PP.EVENTS.variantChange, function (detail) {
          if (detail && detail.sectionId && detail.sectionId !== sectionId) return;
          aim();
        })
      );
    }

    live[sectionId] = {
      node: bar,
      stop: function () {
        gate.disconnect();
        latch.disconnect();
        if (sizer) sizer.disconnect();
        off.forEach(function (fn) {
          fn();
        });
        off = [];
        watched = null;
        /* The reserved strip belongs to the bar, so it leaves with it. A section
           deleted in the editor would otherwise strand a blank band at the end of
           the document until the next navigation. */
        document.documentElement.style.setProperty(HEIGHT, '0px');
      }
    };

    aim();
  }

  function init(scope) {
    sweep();
    var node = scope && scope.querySelectorAll ? scope : document;
    if (node.matches && node.matches(ROOT)) setup(node);
    Array.prototype.forEach.call(node.querySelectorAll(ROOT), setup);
  }

  init(document);

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', function () {
    sweep();
  });
})();
